import {
  computed,
  Component,
  DestroyRef,
  inject,
  Input,
  InputSignal,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DEFAULT_TOASTER_LIFE,
  fieldInvalid,
  ZendraLocalizationServices,
  ZnConfirmation,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import { finalize, Observable, startWith } from 'rxjs';

interface DeleteMeasurementService {
  deleteByMeditionNumber(request: { meditionNumber: number }): Observable<unknown>;
}

interface RunRoutineService {
  executeLidarInventoryRoutine(request: { meditionNumbers: number[] }): Observable<unknown>;
}

interface MeasurementCatalogService {
  useLidar?: boolean;
  loadMeditionNumbers(
    params?: ZnSelectQueryParams,
  ): Observable<{ items: { key: string; value: string }[]; totalCount: number }>;
}

@Component({
  selector: 'lib-action-delete-measurement',
  imports: [LocalizationPipe, CommonModule, ReactiveFormsModule, ZnSingleSelect],
  templateUrl: './action-delete-measurement.html',
})
export class ActionDeleteMeasurement implements IModalContent {
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly formBuilder = inject(FormBuilder);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toaster = inject(ToasterService);

  loading = signal(false);
  saving = signal(false);
  measurementOptions = signal<{ key: string; value: string }[]>([]);
  completed = output<void>();

  localizationResource = ZendraLocalizationServices.Inventory;
  languageLocalizationResource = ZendraLocalizationServices.LanguageService;
  selectedRecords?: InputSignal<any[]>;
  selectedRecordIds?: InputSignal<number[]>;

  @Input() deleteService?: DeleteMeasurementService;
  @Input() runRoutineService?: RunRoutineService;
  @Input({ required: true }) catalogService!: MeasurementCatalogService;
  @Input({ required: true }) measurementLabelKey!: string;
  @Input({ required: true }) successMessageKey!: string;
  @Input() isRunRoutineMode = false;
  @Input() useLidar = false;
  @Input() successToastLife = DEFAULT_TOASTER_LIFE;

  form: FormGroup = this.formBuilder.group({
    MeditionNumber: [null, [Validators.required]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status,
  });

  canSave = computed(() => {
    this.formStatus();
    return this.form.valid;
  });

  fieldInvalidFunction = fieldInvalid;
  readonly meditionNumbers = (params?: ZnSelectQueryParams) => {
    this.catalogService.useLidar = this.useLidar;
    return this.catalogService.loadMeditionNumbers(params);
  };

  delete() {
    if (this.loading()) return;
    const request = this.getSelectedMeditionNumber();
    const deleteRequest = this.deleteService?.deleteByMeditionNumber({ meditionNumber: request });

    if (!deleteRequest) {
      return;
    }

    this.loading.set(true);

    deleteRequest.pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => { this.loading.set(false); })
    )
    .subscribe({
      next: () => {
        this.handleSuccess();
      },
      error: error => {
        console.log(error);
      },
    });
  }

  runRoutine() {
    if (this.loading()) return;
    const request = [this.getSelectedMeditionNumber()];
    const runRoutineRequest = this.runRoutineService?.executeLidarInventoryRoutine({
      meditionNumbers: request,
    });

    if (!runRoutineRequest) {
      return;
    }

    this.loading.set(true);

    runRoutineRequest.pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => { this.loading.set(false); })
    )
    .subscribe({
      next: () => {
        this.handleSuccess();
      },
      error: error => {
        console.log(error, request);
      },
    });
  }

  confirm(): void {
    if (this.isRunRoutineMode) {
      this.runRoutine();
      return;
    }

    this.delete();
  }

  dismiss(): void {
    if (this.saving() || this.loading()) {
      return;
    }

    if (this.form.dirty) {
      this.confirmationService.showCancelConfirmation().subscribe(status => {
        if (status === Confirmation.Status.confirm) {
          this.form.reset();
          this.activeModal.dismiss();
        }
      });
    } else {
      this.activeModal.dismiss();
    }
  }

  private getSelectedMeditionNumber(): number {
    return Number(this.form.getRawValue()['MeditionNumber']);
  }

  private handleSuccess(): void {
    this.toaster.success(`${this.localizationResource}::${this.successMessageKey}`, null, {
      life: this.successToastLife,
    });
    this.completed.emit();
    this.activeModal.close();
  }
}
